import { type ReactElement } from 'react';
import { Box, Stack, TableColumns } from '@ff-it/ui';
import { ButtonGroupField, IndexContainer } from 'components';
import type { GenericTask, TaskResponse, TaskStatus } from './types';
import * as c from './columns';
import { TaskStatusSelector } from './TaskStatusSelector';
import { TableFilter, useTable } from 'components/ControlledTable';
import { fetcher } from 'services';
import { actionErrorOrThrow } from 'utilities';
import { useIndicatorInvalidate } from 'core/Indicators';

function TableTaskStatusSelector({ task: { id, status, object } }: { task: GenericTask }): ReactElement {
  const { mutate } = useTable<GenericTask>();
  const invalidate = useIndicatorInvalidate();
  return (
    <TaskStatusSelector
      state={status}
      onStatusChange={async (status: TaskStatus) => {
        await mutate(async (data) => {
          await fetcher<TaskResponse>({
            method: 'PATCH',
            url: `tasks/${object.app}:${object.model}:${object.id}/${id}/`,
            body: { status },
          }).catch(actionErrorOrThrow);
          return data;
        });
        await invalidate('late_tasks');
      }}
    />
  );
}

const columns: TableColumns<GenericTask> = [
  {
    key: 'status',
    width: '130px',
    render: (task) => <TableTaskStatusSelector task={task} />,
  },
  c.name,
  c.context,
  c.author,
  c.created,
  c.dueDate,
  c.assignees,
  c.topic,
];

export function MyTasks(): ReactElement {
  return (
    <IndexContainer
      defaultFilter={{
        relationship: 'ASSIGNED',
        status: 'ALL',
      }}
      filterFields={null}
      before={
        <Box display="flex" mt="2">
          <TableFilter hideClear>
            <Stack direction="horizontal">
              <ButtonGroupField
                name="relationship"
                variant="secondary"
                options={[
                  { value: 'ASSIGNED', label: 'Assigned to me' },
                  { value: 'CREATED', label: 'Created by me' },
                ]}
              />
              <ButtonGroupField
                name="status"
                variant="secondary"
                options={[
                  { value: 'ALL', label: 'All' },
                  { value: 'OPEN', label: 'Open' },
                  { value: 'CLOSED', label: 'Closed' },
                ]}
              />
            </Stack>
          </TableFilter>
        </Box>
      }
      title="My tasks"
      breadCrumb="My tasks"
      url="tasks/dashboard/my_tasks/"
      columns={columns}
    />
  );
}
